import { FC } from 'react';

import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { usePopover } from '../../hooks/use-popover';

import {
  PrivacyTip as PrivacyIcon,
  StickyNote2 as StickyNoteIcon,
} from '@mui/icons-material';
import { ChevronDown as ChevronDownIcon } from '../../icons/chevron-down';
import { Logout as LogoutIcon } from '../../icons/logout';
import { useUser } from 'src/containers/contexts/auth-provider';
import { AccountPopoverAvatar } from '../molecules/navbar/account-popover-avatar';
import { useSignOut } from 'src/hooks/api/auth/use-sign-out';
import { LoaderIcon } from 'react-hot-toast';

const AccountPopoverComponent: FC = ({ ...other }) => {
  const { i18n, t } = useTranslation();

  const user = useUser();

  const [anchorRef, open, handleOpen, handleClose] = usePopover();

  const { signOut, isSigningOut } = useSignOut();

  //to be updated
  const handleOpenPrivacyPolicy = () => {
    window.open(
      `https://storage.googleapis.com/acs-assets/privacy-policy-${i18n.language}.pdf`,
      '_blank',
    );
  };

  //to be updated
  const handleOpenTermsAndConditions = () => {
    window.open(
      `https://storage.googleapis.com/acs-assets/terms-of-use-${i18n.language}.pdf`,
      '_blank',
    );
  };

  const renderAvatar = (asListItem = false) => {
    return (
      <AccountPopoverAvatar
        avatarText={user?.email?.charAt(0).toUpperCase() ?? ''}
        asListItem={asListItem}
      />
    );
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        ref={anchorRef}
        sx={{
          alignItems: 'center',
          cursor: 'pointer',
          display: 'flex',
          ml: 2,
        }}
        {...other}>
        {renderAvatar()}
        <Box
          sx={{
            alignItems: 'center',
            display: {
              md: 'flex',
              xs: 'none',
            },
            flex: 1,
            ml: 1,
            minWidth: 120,
          }}>
          <div>
            <Typography sx={{ color: 'primary.contrast' }} variant="subtitle2">
              {user?.email}
            </Typography>
          </div>
          <ChevronDownIcon
            sx={{
              color: 'primary.contrast',
              ml: 1,
            }}
          />
        </Box>
      </Box>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            minWidth: 260,
            display: 'flex',
            flexDirection: 'column',
          },
        }}>
        <List sx={{ p: 0 }}>
          <ListItem>
            <ListItemAvatar>{renderAvatar(true)}</ListItemAvatar>
            <ListItemText primary={user?.email} />
          </ListItem>
          <ListItemButton onClick={handleOpenTermsAndConditions}>
            <ListItemIcon>
              <PrivacyIcon />
            </ListItemIcon>
            <ListItemText primary={t('common:termsAndConditions')} />
          </ListItemButton>
          <ListItemButton onClick={handleOpenPrivacyPolicy}>
            <ListItemIcon>
              <StickyNoteIcon />
            </ListItemIcon>
            <ListItemText primary={t('common:privacyPolicy')} />
          </ListItemButton>
          <ListItemButton
            disabled={isSigningOut}
            onClick={() => signOut(handleClose)}>
            <ListItemIcon>
              {isSigningOut ? (
                <LoaderIcon style={{ height: 18, width: 18 }} />
              ) : (
                <LogoutIcon />
              )}
            </ListItemIcon>
            <ListItemText primary={t('common:logout')} />
          </ListItemButton>
        </List>
      </Popover>
    </>
  );
};

export const AccountPopover = AccountPopoverComponent;
